import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, X, ChevronDown, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { PHP_API } from '../config/api';

const seasons = ['Rabi (Winter)', 'Kharif (Monsoon)', 'Year-round', 'Rabi/Summer'];
const regions = ['North India', 'South India', 'Central India', 'West India', 'East India', 'All India'];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    crop_type: searchParams.get('crop_type') || '',
    season: searchParams.get('season') || '',
    region: searchParams.get('region') || '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(PHP_API.categories);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const page = searchParams.get('page') || 1;
      params.append('page', page);
      params.append('page_size', 12);

      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('crop_type')) params.append('crop_type', searchParams.get('crop_type'));
      if (searchParams.get('season')) params.append('season', searchParams.get('season'));
      if (searchParams.get('region')) params.append('region', searchParams.get('region'));

      const response = await axios.get(`${PHP_API.products}?${params.toString()}`);
      setProducts(response.data.products);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages,
      });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.crop_type && filters.crop_type !== 'all') params.set('crop_type', filters.crop_type);
    if (filters.season && filters.season !== 'all') params.set('season', filters.season);
    if (filters.region && filters.region !== 'all') params.set('region', filters.region);
    params.set('page', '1');
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ search: '', crop_type: '', season: '', region: '' });
    setSearchParams({ page: '1' });
    setShowFilters(false);
  };

  const goToPage = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = (filters.crop_type && filters.crop_type !== 'all') || 
                          (filters.season && filters.season !== 'all') || 
                          (filters.region && filters.region !== 'all') || 
                          filters.search;

  return (
    <div className="page-transition min-h-screen bg-stone-50">
      {/* Header */}
      <section className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ label: 'Products' }]} />
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-2">
                Our Seed Collection
              </h1>
              <p className="text-stone-600">
                Discover premium quality seeds for all your agricultural needs
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-12 h-12 rounded-full border-stone-200"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-12 px-4 rounded-full border-stone-200"
                data-testid="filter-btn"
              >
                <Filter className="w-5 h-5" />
                <span className="ml-2 hidden sm:inline">Filters</span>
                {hasActiveFilters && (
                  <span className="ml-2 w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center">
                    !
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-stone-100"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="form-label">Crop Type</label>
                  <Select
                    value={filters.crop_type || "all"}
                    onValueChange={(value) => setFilters({ ...filters, crop_type: value === "all" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-crop-type">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="form-label">Season</label>
                  <Select
                    value={filters.season || "all"}
                    onValueChange={(value) => setFilters({ ...filters, season: value === "all" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-season">
                      <SelectValue placeholder="All Seasons" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Seasons</SelectItem>
                      {seasons.map((season) => (
                        <SelectItem key={season} value={season}>{season}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="form-label">Region</label>
                  <Select
                    value={filters.region || "all"}
                    onValueChange={(value) => setFilters({ ...filters, region: value === "all" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-region">
                      <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {regions.map((region) => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end gap-3">
                  <Button onClick={applyFilters} className="btn-primary flex-1" data-testid="apply-filters-btn">
                    Apply Filters
                  </Button>
                  {hasActiveFilters && (
                    <Button onClick={clearFilters} variant="outline" className="px-4" data-testid="clear-filters-btn">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap gap-2">
              {filters.search && (
                <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm">
                  Search: {filters.search}
                  <button onClick={() => { setFilters({ ...filters, search: '' }); }}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.crop_type && filters.crop_type !== 'all' && (
                <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm">
                  {filters.crop_type}
                  <button onClick={() => { setFilters({ ...filters, crop_type: '' }); }}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.season && filters.season !== 'all' && (
                <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm">
                  {filters.season}
                  <button onClick={() => { setFilters({ ...filters, season: '' }); }}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.region && filters.region !== 'all' && (
                <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm">
                  {filters.region}
                  <button onClick={() => { setFilters({ ...filters, region: '' }); }}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-stone-400" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-stone-900 mb-2">No products found</h3>
              <p className="text-stone-600 mb-6">Try adjusting your search or filter criteria</p>
              <Button onClick={clearFilters} className="btn-primary">
                Clear All Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-stone-600">
                  Showing <span className="font-semibold text-stone-900">{products.length}</span> of{' '}
                  <span className="font-semibold text-stone-900">{pagination.total}</span> products
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    disabled={pagination.page === 1}
                    onClick={() => goToPage(pagination.page - 1)}
                    className="px-4"
                    data-testid="prev-page-btn"
                  >
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.total_pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.total_pages - 2) {
                        pageNum = pagination.total_pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={pagination.page === pageNum ? 'default' : 'outline'}
                          onClick={() => goToPage(pageNum)}
                          className={`w-10 h-10 p-0 ${pagination.page === pageNum ? 'bg-primary' : ''}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    disabled={pagination.page === pagination.total_pages}
                    onClick={() => goToPage(pagination.page + 1)}
                    className="px-4"
                    data-testid="next-page-btn"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}


